import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { request } from "./request";
import {
	getCustomerName,
	isValidVnMobile,
	normalizePhone,
} from "./utilsBirthday";

dayjs.extend(utc);

export const WEBHOOK_THU_TIEN_FIELD = "link_webhook_thu_tien";
export const ZNS_THU_TIEN_FIELD = "zns_thu_tien";

export const REASON_TEXT = {
	sdt_khong_hop_le: "SĐT không hợp lệ",
	khach_khong_co_sdt: "khách chưa có SĐT",
	khong_tim_thay_khach: "không tìm thấy hồ sơ khách",
	khong_co_ma_kh: "phiếu thu không gắn khách hàng",
	so_tien_khong_hop_le: "số tiền không hợp lệ",
	gui_that_bai: "gửi thất bại",
	doc_phieu_loi: "không đọc được phiếu thu",
};

// Lý do sửa được bằng cách nhập lại SĐT
export const PHONE_REASONS = ["sdt_khong_hop_le", "khach_khong_co_sdt"];

const getHeaders = () => ({
	"X-Access-Token": localStorage.getItem("token"),
	"Content-Type": "application/json",
});

const sumTien = (rows) =>
	Array.isArray(rows)
		? rows.reduce((total, row) => total + (Number(row?.tien_nt) || 0), 0)
		: 0;

const getAmount = (pt1) => {
	const total = Number(pt1?.t_tt);
	if (total > 0) return total;
	return sumTien(pt1?.details) || sumTien(pt1?.tdttnos);
};

const fetchLinkWebhook = async (idApp) => {
	const res = await request.get("api/app", {
		params: { q: JSON.stringify({ _id: idApp }), notfields: "__v" },
		headers: getHeaders(),
	});
	return res?.data?.[0]?.exfields?.[WEBHOOK_THU_TIEN_FIELD] || null;
};

export const findCustomerByMa = async (maKh) => {
	if (!maKh) return null;
	const idApp = localStorage.getItem("id_app");
	const res = await request.get(`api/${idApp}/customer`, {
		params: { q: JSON.stringify({ ma_kh: maKh }), limit: 1 },
		headers: getHeaders(),
	});
	return Array.isArray(res?.data) ? res.data[0] || null : null;
};

export const getThuTienZns = (pt1) => ({
	status: pt1?.exfields?.[ZNS_THU_TIEN_FIELD] || null,
	reason: pt1?.exfields?.[`${ZNS_THU_TIEN_FIELD}_loi`] || null,
	at: pt1?.exfields?.[`${ZNS_THU_TIEN_FIELD}_luc`] || null,
});

// Ghi kết quả gửi lên pt1.exfields (PUT cả bản ghi mới nhất, giống form sửa phiếu thu).
const saveFlag = async ({ latest, status, reason }) => {
	const idApp = localStorage.getItem("id_app");
	const exfields = {
		...latest?.exfields,
		[ZNS_THU_TIEN_FIELD]: status,
		[`${ZNS_THU_TIEN_FIELD}_luc`]: new Date().toISOString(),
	};
	if (reason) {
		exfields[`${ZNS_THU_TIEN_FIELD}_loi`] = reason;
	} else {
		delete exfields[`${ZNS_THU_TIEN_FIELD}_loi`];
	}
	try {
		await request.put(
			`api/${idApp}/pt1/${latest._id}`,
			{ ...latest, exfields },
			{ headers: getHeaders() },
		);
		return { exfields, saved: true };
	} catch (e) {
		console.error("Khong ghi duoc co ZNS thu tien", latest?._id, e);
		return { exfields, saved: false };
	}
};

// Gửi ZNS thông báo đã thu tiền cho khách của 1 phiếu thu.
// Trả về { status: "sent" | "failed" | "disabled", reason, exfields, saved }
export const sendThuTienZns = async (pt1Id) => {
	const idApp = localStorage.getItem("id_app");
	if (!idApp || !localStorage.getItem("token") || !pt1Id) {
		return { status: "failed", reason: "doc_phieu_loi" };
	}

	let linkWebhook = null;
	try {
		linkWebhook = await fetchLinkWebhook(idApp);
	} catch (e) {
		console.error("Khong lay duoc link webhook thu tien", e);
		return { status: "failed", reason: "gui_that_bai" };
	}
	if (!linkWebhook) return { status: "disabled" };

	let latest;
	try {
		const res = await request.get(`api/${idApp}/pt1/${pt1Id}`, {
			headers: getHeaders(),
		});
		latest = res?.data && !Array.isArray(res.data) ? res.data : null;
	} catch (e) {
		console.error("Khong doc duoc phieu thu", pt1Id, e);
	}
	if (!latest?._id) return { status: "failed", reason: "doc_phieu_loi" };

	const fail = async (reason) => ({
		status: "failed",
		reason,
		...(await saveFlag({ latest, status: "failed", reason })),
	});

	if (!latest.ma_kh) return fail("khong_co_ma_kh");
	const amount = getAmount(latest);
	if (!(amount > 0)) return fail("so_tien_khong_hop_le");

	let customer = null;
	try {
		customer = await findCustomerByMa(latest.ma_kh);
	} catch (e) {
		console.error("Khong doc duoc khach", latest.ma_kh, e);
		return fail("khong_tim_thay_khach");
	}
	if (!customer) return fail("khong_tim_thay_khach");
	if (!String(customer.dien_thoai ?? "").trim()) {
		return fail("khach_khong_co_sdt");
	}
	if (!isValidVnMobile(customer.dien_thoai)) return fail("sdt_khong_hop_le");

	const ngayCt = dayjs(latest.ngay_ct);
	try {
		await axios.post(
			linkWebhook,
			{
				customer_name:
					getCustomerName(latest) ||
					getCustomerName(customer) ||
					latest.ten_kh,
				phone: normalizePhone(customer.dien_thoai),
				t_tien: amount,
				ngay_ct: (ngayCt.isValid() ? ngayCt : dayjs())
					.utcOffset(7 * 60)
					.format("DD/MM/YYYY"),
				so_ct: String(latest.so_ct ?? ""),
			},
			{ headers: { "Content-Type": "application/json" } },
		);
	} catch (e) {
		console.error("Gui ZNS thu tien loi", pt1Id, e);
		return fail("gui_that_bai");
	}
	return {
		status: "sent",
		...(await saveFlag({ latest, status: "sent", reason: null })),
	};
};

// Gọi sau khi tạo pt1 thành công: gửi ZNS và báo kết quả cho admin trên màn hình.
// Không bao giờ throw để không ảnh hưởng việc tạo phiếu thu.
export const notifyThuTienAfterCreate = async (pt1, messageApi) => {
	try {
		const result = await sendThuTienZns(pt1?._id);
		if (result.status === "disabled") return;
		if (result.status === "sent") {
			messageApi?.open?.({
				type: "success",
				content: "Đã gửi ZNS cho khách",
			});
			return;
		}
		messageApi?.open?.({
			type: "warning",
			duration: 6,
			content: `Đã tạo phiếu thu nhưng chưa gửi ZNS: ${
				REASON_TEXT[result.reason] || "gửi thất bại"
			}`,
		});
	} catch (e) {
		console.error("Loi gui ZNS thu tien", e);
	}
};

// Điều kiện lọc danh sách phiếu thu theo trạng thái ZNS: all | chua | failed | sent
export const getZnsFilterQuery = (value) => {
	const key = `exfields.${ZNS_THU_TIEN_FIELD}`;
	if (value === "sent") return { [key]: "sent" };
	if (value === "failed") return { [key]: "failed" };
	if (value === "chua") return { [key]: { $ne: "sent" } };
	return {};
};
